import { useMemo } from 'react'
import PropTypes from 'prop-types'
import ReactECharts from 'echarts-for-react'
import {
  createEducationOwnershipDonutOption,
  createLocationComparisonOption,
  createGenderBalanceDonutOption,
  createInfrastructureBarOption,
  createResourceRatiosCardsData,
  createTeacherStudentRatioBarOption,
  createWASHComplianceOption,
} from '../../utils/educationChartUtils'

function EducationCharts({ chartData, filtered }) {
  // Education-specific chart options
  const educationOwnershipDonutOption = useMemo(() => {
    if (!chartData?.ownershipDistribution) return null
    return createEducationOwnershipDonutOption(
      chartData.ownershipDistribution,
      'Education Ownership Distribution',
      `${filtered}\ninstitutions`
    )
  }, [chartData, filtered])

  const locationHierarchyComparisonOption = useMemo(() => {
    if (!chartData?.locationHierarchyDistribution?.data?.length) return null
    return createLocationComparisonOption(
      chartData.locationHierarchyDistribution.data,
      '' // Title will be in HTML
    )
  }, [chartData])

  const genderBalanceDonutOption = useMemo(() => {
    if (!chartData?.genderBalance || chartData.genderBalance.total === 0)
      return null
    return createGenderBalanceDonutOption(
      chartData.genderBalance,
      'Student Gender Distribution'
    )
  }, [chartData])

  const infrastructureBarOption = useMemo(() => {
    if (!chartData?.infrastructureReadiness?.length) return null
    return createInfrastructureBarOption(
      chartData.infrastructureReadiness,
      'Infrastructure Coverage'
    )
  }, [chartData])

  // New infrastructure chart options
  const resourceRatiosCardsData = useMemo(() => {
    if (!chartData?.resourceRatios) return null
    return createResourceRatiosCardsData(chartData.resourceRatios)
  }, [chartData])

  const teacherStudentRatioBarOption = useMemo(() => {
    if (!chartData?.teacherStudentRatios?.length) return null
    return createTeacherStudentRatioBarOption(chartData.teacherStudentRatios)
  }, [chartData])

  const washComplianceOption = useMemo(() => {
    if (!chartData?.washCompliance) return null
    return createWASHComplianceOption(chartData.washCompliance)
  }, [chartData])

  if (!chartData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Loading education analytics...</p>
      </div>
    )
  }

  return (
    <>
      {/* Ownership Distribution Donut Chart */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
          Educational Ownership Distribution
        </h3>
        <div className="h-80">
          {educationOwnershipDonutOption && (
            <ReactECharts
              option={educationOwnershipDonutOption}
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
              theme="ug-dashboard"
            />
          )}
        </div>
        <div className="mt-4 text-sm text-gray-600">
          <p className="font-medium">How is education provision distributed?</p>
          <p className="mt-2">
            This chart shows the balance between government and private
            educational institutions. A higher government percentage indicates
            stronger public education investment.
          </p>
        </div>
      </div>

      {/* Dynamic Location Hierarchy Comparison */}
      {chartData?.locationHierarchyDistribution &&
        chartData.locationHierarchyDistribution.level !== 'none' && (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
              {chartData.locationHierarchyDistribution.title}
            </h3>
            <div className="h-96">
              {locationHierarchyComparisonOption && (
                <ReactECharts
                  option={locationHierarchyComparisonOption}
                  style={{ height: '100%', width: '100%' }}
                  opts={{ renderer: 'canvas' }}
                  theme="ug-dashboard"
                />
              )}
            </div>
            <div className="mt-4 text-sm text-gray-600">
              <p className="font-medium">
                {chartData.locationHierarchyDistribution.description}
              </p>
              <p className="mt-2">
                This comparison shows the distribution across{' '}
                {chartData.locationHierarchyDistribution.level}, helping
                identify areas with limited education access.
              </p>
            </div>
          </div>
        )}

      {/* Individual Institution View Message */}
      {chartData?.locationHierarchyDistribution?.level === 'none' && (
        <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold text-blue-900 mb-4 text-center">
            {chartData.locationHierarchyDistribution.title}
          </h3>
          <div className="text-center text-blue-700">
            <p className="font-medium mb-2">
              {chartData.locationHierarchyDistribution.description}
            </p>
            <p className="text-sm">
              At this level of detail, the Grid view provides the most useful
              information about individual institutions.
            </p>
          </div>
        </div>
      )}

      {/* Student Gender Distribution */}
      {genderBalanceDonutOption && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
            Student Gender Distribution
          </h3>
          <div className="h-80">
            <ReactECharts
              option={genderBalanceDonutOption}
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
              theme="ug-dashboard"
            />
          </div>
          <div className="mt-4 text-sm text-gray-600">
            <p className="font-medium">How balanced is gender enrollment?</p>
            <p className="mt-2">
              This chart shows the overall gender distribution across all
              students. Gender parity in education is crucial for equal
              opportunity and development.
            </p>
            {chartData?.genderBalance && (
              <div className="mt-3 grid grid-cols-2 gap-4 text-xs">
                <div className="text-center bg-blue-50 p-2 rounded">
                  <div className="font-semibold text-blue-700">Boys</div>
                  <div className="text-lg">
                    {chartData.genderBalance.boys_percentage}%
                  </div>
                </div>
                <div className="text-center bg-pink-50 p-2 rounded">
                  <div className="font-semibold text-pink-700">Girls</div>
                  <div className="text-lg">
                    {chartData.genderBalance.girls_percentage}%
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Infrastructure Coverage */}
      {infrastructureBarOption && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
            School Infrastructure Coverage
          </h3>
          <div className="h-96">
            <ReactECharts
              option={infrastructureBarOption}
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
              theme="ug-dashboard"
            />
          </div>
          <div className="mt-4 text-sm text-gray-600">
            <p className="font-medium">
              What basic infrastructure is available?
            </p>
            <p className="mt-2">
              This chart shows the percentage of schools that have essential
              infrastructure like electricity, water, toilets, and ICT
              facilities. Higher coverage indicates better learning conditions.
            </p>
          </div>
        </div>
      )}

      {/* Resource Ratios Dashboard */}
      {resourceRatiosCardsData && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">
            📊 Resource Adequacy Dashboard
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            {resourceRatiosCardsData.map((card, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white p-4 rounded-lg border-2 border-gray-100 hover:shadow-md transition-shadow"
              >
                {/* Card Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="text-2xl">{card.icon}</div>
                  <div
                    className={`px-2 py-1 rounded-full text-xs font-medium text-white`}
                    style={{ backgroundColor: card.statusColor }}
                  >
                    {card.statusText}
                  </div>
                </div>

                {/* Main Metric */}
                <div className="mb-2">
                  <div className="text-2xl font-bold text-gray-900">
                    {card.value.toFixed(2)}
                    {card.unit}
                  </div>
                  <div className="text-sm text-gray-600 font-medium">
                    {card.name}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Current</span>
                    <span>
                      Target: {card.benchmark}
                      {card.unit}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${Math.min(card.percentage, 100)}%`,
                        backgroundColor: card.statusColor,
                      }}
                    ></div>
                  </div>
                </div>

                {/* Description */}
                <div className="text-xs text-gray-500">{card.description}</div>

                {/* Comparison to Benchmark */}
                <div className="mt-2 text-xs">
                  {card.value <= card.benchmark ? (
                    <span className="text-green-600 font-medium">
                      ✓ Within recommended ratio
                    </span>
                  ) : (
                    <span className="text-red-600 font-medium">
                      ⚠ {(card.value / card.benchmark).toFixed(1)}x above
                      target
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 text-sm text-gray-600">
            <p className="font-medium">
              How adequate are educational resources?
            </p>
            <p className="mt-2">
              These cards show key resource ratios compared to educational
              standards. Lower ratios (closer to targets) indicate better
              resource adequacy. Progress bars show how current ratios compare
              to recommended benchmarks.
            </p>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              <div className="bg-green-50 p-2 rounded">
                <div className="font-semibold text-green-700">Excellent</div>
                <div>At or below target</div>
              </div>
              <div className="bg-blue-50 p-2 rounded">
                <div className="font-semibold text-blue-700">Good</div>
                <div>Within 25% of target</div>
              </div>
              <div className="bg-yellow-50 p-2 rounded">
                <div className="font-semibold text-yellow-700">Fair</div>
                <div>Within 50% of target</div>
              </div>
              <div className="bg-red-50 p-2 rounded">
                <div className="font-semibold text-red-700">Critical</div>
                <div>Over 50% above target</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Teacher-Student Ratio Analysis */}
      {teacherStudentRatioBarOption && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
            👩‍🏫 Teacher-Student Ratio Analysis
          </h3>
          <div className="h-96">
            <ReactECharts
              option={teacherStudentRatioBarOption}
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
              theme="ug-dashboard"
            />
          </div>
          <div className="mt-4 text-sm text-gray-600">
            <p className="font-medium">
              Which schools have the highest teacher burden?
            </p>
            <p className="mt-2">
              This chart shows schools with the highest pupil-teacher ratios.
              Schools in red exceed recommended ratios and may struggle with
              overcrowded classrooms and teacher workload.
            </p>
            <div className="mt-3 grid grid-cols-3 gap-4 text-xs">
              <div className="text-center bg-green-50 p-2 rounded">
                <div className="font-semibold text-green-700">Good</div>
                <div>≤ 40:1 ratio</div>
              </div>
              <div className="text-center bg-yellow-50 p-2 rounded">
                <div className="font-semibold text-yellow-700">Strained</div>
                <div>41-50:1 ratio</div>
              </div>
              <div className="text-center bg-red-50 p-2 rounded">
                <div className="font-semibold text-red-700">Overcrowded</div>
                <div>&gt; 50:1 ratio</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* WASH Compliance Overview */}
      {washComplianceOption && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
            🚿 WASH Compliance Overview
          </h3>
          <div className="h-80">
            <ReactECharts
              option={washComplianceOption}
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
              theme="ug-dashboard"
            />
          </div>
          <div className="mt-4 text-sm text-gray-600">
            <p className="font-medium">
              How safe and hygienic are learning environments?
            </p>
            <p className="mt-2">
              WASH (Water, Sanitation, and Hygiene) facilities are essential for
              student health and attendance. Schools above 80% coverage (target
              line) provide safer learning environments.
            </p>
            <div className="mt-3 grid grid-cols-2 gap-4 text-xs">
              <div className="bg-green-50 p-2 rounded">
                <div className="font-semibold text-green-700">
                  Good Coverage
                </div>
                <div>≥ 80% of schools</div>
              </div>
              <div className="bg-yellow-50 p-2 rounded">
                <div className="font-semibold text-yellow-700">
                  Needs Improvement
                </div>
                <div>&lt; 80% of schools</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Infrastructure Gap Matrix */}
      {chartData?.infrastructureGapMatrix && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
            🏗️ Infrastructure Gap Matrix
          </h3>

          {/* Summary Statistics */}
          <div className="mb-6 grid grid-cols-2 md:grid-cols-2 gap-4 text-sm">
            <div className="text-center bg-blue-50 p-3 rounded">
              <div className="font-semibold text-blue-700">
                Schools Analyzed
              </div>
              <div className="text-lg">
                {chartData.infrastructureGapMatrix.summary.totalSchools}
              </div>
            </div>
            <div className="text-center bg-green-50 p-3 rounded">
              <div className="font-semibold text-green-700">
                Avg Completeness
              </div>
              <div className="text-lg">
                {chartData.infrastructureGapMatrix.summary.avgCompleteness}%
              </div>
            </div>
            <div className="text-center bg-yellow-50 p-3 rounded">
              <div className="font-semibold text-yellow-700">Best School</div>
              <div className="text-sm">
                {chartData.infrastructureGapMatrix.summary.bestSchool}
              </div>
            </div>
            <div className="text-center bg-red-50 p-3 rounded">
              <div className="font-semibold text-red-700">Needs Most Help</div>
              <div className="text-sm">
                {chartData.infrastructureGapMatrix.summary.worstSchool}
              </div>
            </div>
          </div>

          {/* Heatmap Matrix */}
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
              {/* Header */}
              <thead>
                <tr>
                  <th className="border border-gray-300 bg-gray-100 px-4 py-3 text-left text-sm font-medium text-gray-700 min-w-48">
                    School Name
                  </th>
                  <th className="border border-gray-300 bg-gray-100 px-3 py-3 text-center text-xs font-medium text-gray-700 w-20">
                    Score
                  </th>
                  {chartData.infrastructureGapMatrix.categories.map(
                    category => (
                      <th
                        key={category.key}
                        className="border border-gray-300 bg-gray-100 px-3 py-3 text-center text-xs font-medium text-gray-700 w-16"
                        title={category.name}
                      >
                        {category.name}
                      </th>
                    )
                  )}
                </tr>
              </thead>

              {/* Rows */}
              <tbody>
                {chartData.infrastructureGapMatrix.schools.map(
                  (school, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td
                        className="border border-gray-300 px-4 py-3 text-sm"
                        title={school.fullName}
                      >
                        <div className="font-medium text-gray-900">
                          {school.name}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {school.type} • {school.ownership}
                        </div>
                      </td>
                      <td className="border border-gray-300 px-3 py-3 text-center">
                        <span
                          className={`inline-block px-2 py-1 rounded text-xs font-medium text-white ${
                            school.completeness >= 80
                              ? 'bg-green-500'
                              : school.completeness >= 60
                                ? 'bg-yellow-500'
                                : school.completeness >= 40
                                  ? 'bg-orange-500'
                                  : 'bg-red-500'
                          }`}
                        >
                          {school.completeness}%
                        </span>
                      </td>
                      {chartData.infrastructureGapMatrix.categories.map(
                        category => (
                          <td
                            key={category.key}
                            className="border border-gray-300 px-3 py-3 text-center"
                          >
                            <div
                              className={`w-8 h-8 mx-auto rounded flex items-center justify-center ${
                                school[category.key] === 1
                                  ? 'bg-green-500 text-white'
                                  : 'bg-red-400 text-white'
                              }`}
                              title={`${category.name}: ${school[category.key] === 1 ? 'Available' : 'Not Available'}`}
                            >
                              <span className="text-xs font-bold">
                                {school[category.key] === 1 ? '✓' : '✗'}
                              </span>
                            </div>
                          </td>
                        )
                      )}
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div className="mt-4 flex flex-wrap gap-4 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-500 rounded"></div>
              <span>Available</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-400 rounded"></div>
              <span>Not Available</span>
            </div>
            <div className="ml-6 flex items-center gap-4">
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span>80%+</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                <span>60-79%</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 bg-orange-500 rounded"></div>
                <span>40-59%</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span>&lt;40%</span>
              </div>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            <p className="font-medium">
              Which infrastructure gaps are most common?
            </p>
            <p className="mt-2">
              This matrix shows the availability of key infrastructure across
              schools. Green cells indicate available infrastructure, red cells
              show gaps. Schools are sorted by overall completeness score
              (percentage of infrastructure available).
            </p>
          </div>
        </div>
      )}

      {/* Teacher Qualifications Overview */}
      {chartData?.teacherQualifications?.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
            Teacher Qualification Distribution
          </h3>
          <div className="grid grid-cols-3 gap-6">
            {chartData.teacherQualifications.map((qual, index) => (
              <div key={qual.name} className="text-center">
                <div
                  className={`text-3xl font-bold ${
                    index === 0
                      ? 'text-purple-600'
                      : index === 1
                        ? 'text-blue-600'
                        : 'text-green-600'
                  }`}
                >
                  {qual.percentage}%
                </div>
                <div className="text-sm text-gray-600 mt-1">{qual.name}</div>
                <div className="text-xs text-gray-500">
                  {qual.value} teachers
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 text-sm text-gray-600">
            <p className="font-medium">
              What are teacher qualification levels?
            </p>
            <p className="mt-2">
              Higher qualified teachers (degree holders) typically provide
              better educational outcomes. This shows the distribution of
              teacher qualifications across all schools.
            </p>
          </div>
        </div>
      )}

      {/* Education Summary Statistics */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          📚 Education Summary
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-600">
              {filtered}
            </div>
            <div className="text-sm text-gray-600">Total Institutions</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {chartData?.educationLevelDistribution.find(
                d => d.name === 'Primary'
              )?.value || 0}
            </div>
            <div className="text-sm text-gray-600">Primary Schools</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {chartData?.educationLevelDistribution.find(
                d => d.name === 'Secondary'
              )?.value || 0}
            </div>
            <div className="text-sm text-gray-600">Secondary Schools</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {chartData?.ownershipDistribution.find(
                d => d.name === 'Government'
              )?.value || 0}
            </div>
            <div className="text-sm text-gray-600">Government Schools</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {chartData?.locationHierarchyDistribution?.data?.length || 0}
            </div>
            <div className="text-sm text-gray-600">
              {chartData?.locationHierarchyDistribution?.level === 'districts'
                ? 'Districts'
                : chartData?.locationHierarchyDistribution?.level ===
                    'subcounties'
                  ? 'Subcounties'
                  : chartData?.locationHierarchyDistribution?.level ===
                      'parishes'
                    ? 'Parishes'
                    : chartData?.locationHierarchyDistribution?.level ===
                        'villages'
                      ? 'Villages'
                      : 'Locations'}{' '}
              Covered
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

EducationCharts.propTypes = {
  chartData: PropTypes.shape({
    ownershipDistribution: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.number,
        percentage: PropTypes.number,
      })
    ),
    educationLevelDistribution: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.number,
        percentage: PropTypes.number,
        find: PropTypes.func,
      })
    ),
    locationHierarchyDistribution: PropTypes.shape({
      data: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          count: PropTypes.number,
          length: PropTypes.number,
        })
      ),
      level: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
    }),
    ownershipByLevel: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        government: PropTypes.number,
        private: PropTypes.number,
        unknown: PropTypes.number,
        total: PropTypes.number,
      })
    ),
  }),
  filtered: PropTypes.number,
}

export default EducationCharts
